import {
  chooseNickname,
  logIn,
  register,
  requestReset,
  resetTokenFromUrl,
  setPassword,
  type Session,
} from '../network/Session';

/**
 * The way in: logowanie, rejestracja z kodem, nick i reset hasła.
 *
 * Plain DOM over the canvas rather than anything drawn in the scene: a login
 * form wants real text fields with a password manager behind them, and the game
 * renders into a buffer where text would be a smear.
 *
 * The game does not start behind it. Terrain is streamed around wherever the
 * player is standing, and that is not known until the server says so — loading
 * ground first would mean loading the wrong ground.
 *
 * # Four screens, one overlay
 *
 * Signing in, signing up, choosing a name and resetting a password are the same
 * door in four states rather than four components: they share the frame, the
 * error line and the one rule that matters — nothing behind them starts until
 * a session exists **and** has a name on it.
 */

type Screen = 'login' | 'register' | 'nickname' | 'forgot' | 'reset';

interface Field {
  name: string;
  label: string;
  type: string;
  autocomplete?: string;
  hint?: string;
}

const SCREENS: Record<
  Screen,
  { title: string; submit: string; fields: Field[]; switchTo?: Screen; switchLabel?: string }
> = {
  login: {
    title: 'Wejdź do świata',
    submit: 'wejdź',
    fields: [
      { name: 'email', label: 'adres email', type: 'email', autocomplete: 'username' },
      { name: 'password', label: 'hasło', type: 'password', autocomplete: 'current-password' },
    ],
    switchTo: 'register',
    switchLabel: 'mam kod, chcę założyć konto',
  },
  register: {
    title: 'Nowe konto',
    submit: 'załóż konto',
    fields: [
      { name: 'email', label: 'adres email', type: 'email', autocomplete: 'username' },
      { name: 'password', label: 'hasło', type: 'password', autocomplete: 'new-password' },
      {
        name: 'code',
        label: 'kod aktywacyjny',
        type: 'text',
        hint: 'bez kodu nie da się założyć konta — poproś administratora',
      },
    ],
    switchTo: 'login',
    switchLabel: 'mam już konto',
  },
  nickname: {
    title: 'Jak masz na imię w tym świecie?',
    submit: 'to moje imię',
    fields: [
      {
        name: 'nickname',
        label: 'nick',
        type: 'text',
        autocomplete: 'nickname',
        hint: 'wybierasz raz — inni gracze będą cię tak znać',
      },
    ],
  },
  forgot: {
    title: 'Nowe hasło',
    submit: 'wyślij link',
    fields: [{ name: 'email', label: 'adres email', type: 'email', autocomplete: 'username' }],
    switchTo: 'login',
    switchLabel: 'wróć do logowania',
  },
  reset: {
    title: 'Ustaw nowe hasło',
    submit: 'zapisz i wejdź',
    fields: [
      { name: 'password', label: 'nowe hasło', type: 'password', autocomplete: 'new-password' },
    ],
  },
};

export function requestSession(container: HTMLElement, notice?: string): Promise<Session> {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'gate';
    overlay.innerHTML = `
      <form class="gate-form">
        <h1>Uncharted Lands</h1>
        <p class="gate-notice" hidden></p>
        <div class="gate-fields"></div>
        <button type="submit"></button>
        <p class="gate-error" hidden></p>
        <button type="button" class="gate-switch" hidden></button>
        <button type="button" class="gate-forgot" hidden>nie pamiętam hasła</button>
      </form>
    `;

    const form = overlay.querySelector('form') as HTMLFormElement;
    const fields = overlay.querySelector('.gate-fields') as HTMLElement;
    const title = overlay.querySelector('h1') as HTMLElement;
    const submit = overlay.querySelector('button[type="submit"]') as HTMLButtonElement;
    const toggle = overlay.querySelector('.gate-switch') as HTMLButtonElement;
    const forgot = overlay.querySelector('.gate-forgot') as HTMLButtonElement;
    const message = overlay.querySelector('.gate-notice') as HTMLElement;
    const error = overlay.querySelector('.gate-error') as HTMLElement;

    // A link out of a mail lands here with the token in the query string, so
    // the first screen is the one that spends it rather than the login form.
    const resetToken = resetTokenFromUrl();
    let screen: Screen = resetToken ? 'reset' : 'login';
    let session: Session | null = null;

    const say = (text: string | undefined) => {
      message.textContent = text ?? '';
      message.hidden = !text;
    };

    const render = () => {
      const layout = SCREENS[screen];
      title.textContent = layout.title;
      submit.textContent = layout.submit;

      fields.innerHTML = layout.fields
        .map(
          (field) => `
            <label>
              <span>${field.label}</span>
              <input name="${field.name}" type="${field.type}"
                ${field.autocomplete ? `autocomplete="${field.autocomplete}"` : ''} />
              ${field.hint ? `<span class="gate-hint">${field.hint}</span>` : ''}
            </label>`
        )
        .join('');

      toggle.hidden = !layout.switchTo;
      toggle.textContent = layout.switchLabel ?? '';
      // Only where it means something: there is nothing to forget on the screen
      // that is already about forgetting, or on the one asking for a name.
      forgot.hidden = screen !== 'login';

      (fields.querySelector('input') as HTMLInputElement | null)?.focus();
    };

    const go = (next: Screen, note?: string) => {
      screen = next;
      error.hidden = true;
      say(note);
      render();
    };

    const value = (name: string): string =>
      (form.elements.namedItem(name) as HTMLInputElement | null)?.value.trim() ?? '';

    /** A session that has a name on it is the only thing worth resolving with. */
    const settle = (ready: Session) => {
      if (ready.needsNickname) {
        session = ready;
        go('nickname');
        return;
      }
      overlay.remove();
      resolve(ready);
    };

    toggle.addEventListener('click', () => go(SCREENS[screen].switchTo ?? 'login'));
    forgot.addEventListener('click', () => go('forgot'));

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      submit.disabled = true;
      error.hidden = true;

      try {
        switch (screen) {
          case 'login':
            settle(await logIn(value('email'), value('password')));
            break;

          case 'register':
            settle(await register(value('email'), value('password'), value('code')));
            break;

          case 'nickname':
            settle(await chooseNickname(session!, value('nickname')));
            break;

          case 'forgot':
            await requestReset(value('email'));
            // Deliberately the same words whether or not there is an account on
            // that address: the server will not say, and neither will this.
            go('login', 'jeśli konto istnieje, link do zmiany hasła jest już w drodze');
            break;

          case 'reset':
            await setPassword(resetToken!, value('password'));
            go('login', 'hasło zmienione — zaloguj się nowym');
            break;
        }
      } catch (e) {
        // The server's own words: it distinguishes a taken address from a short
        // password from a spent code, and rewriting that here would lose it.
        error.textContent = e instanceof Error ? e.message : String(e);
        error.hidden = false;
      } finally {
        submit.disabled = false;
      }
    });

    say(notice);
    render();
    container.appendChild(overlay);
  });
}
